import React, { useState } from 'react';
import { BookOpen, ExternalLink, Lightbulb, Target, Edit2, Check, X as XIcon, Plus, AlertCircle } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { toast } from 'react-hot-toast';

/**
 * CollegeResearchPanel - Left sidebar showing relevant college data per section
 */
export default function CollegeResearchPanel({
  collegeData,
  currentSection,
  essaySubtype
}) {
  if (!collegeData) {
    return (
      <div className="p-6">
        <div className="text-gray-500 text-sm">Loading college data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section-specific content */}
      {currentSection === 'ethos' && (
        <EthosResearch collegeData={collegeData} />
      )}

      {currentSection === 'academics' && (
        <AcademicsResearch collegeData={collegeData} essaySubtype={essaySubtype} />
      )}

      {currentSection === 'campus' && (
        <CampusResearch collegeData={collegeData} essaySubtype={essaySubtype} />
      )}

      {currentSection === 'personal' && (
        <PersonalResearch collegeData={collegeData} />
      )}

      {(currentSection === 'rhetoric' || currentSection === 'bridges') && (
        <StrategicResearch collegeData={collegeData} />
      )}

      {currentSection === 'compose' && (
        <ComposeResearch collegeData={collegeData} />
      )}
    </div>
  );
}

// Section 1: Ethos Research
function EthosResearch({ collegeData }) {
  const globalData = collegeData.global;
  const [isEditingValues, setIsEditingValues] = useState(false);
  const [editedValues, setEditedValues] = useState(
    collegeData.core_values_override || globalData?.core_values || ''
  );
  const [isEditingMission, setIsEditingMission] = useState(false);
  const [editedMission, setEditedMission] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSaveValues = async () => {
    try {
      setSaving(true);

      // Save to college_journey_colleges (student-specific override)
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ core_values_override: editedValues })
        .eq('id', collegeData.id);

      if (error) throw error;

      toast.success('Core values updated');
      setIsEditingValues(false);

      // Update local state
      collegeData.core_values_override = editedValues;
    } catch (err) {
      console.error('Error saving core values:', err);
      toast.error('Failed to save core values');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMission = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ mission_statement_override: editedMission })
        .eq('id', collegeData.id);

      if (error) throw error;

      toast.success('Mission statement updated');
      setIsEditingMission(false);
      collegeData.mission_statement_override = editedMission;
    } catch (err) {
      console.error('Error saving mission statement:', err);
      toast.error('Failed to save mission statement');
    } finally {
      setSaving(false);
    }
  };

  const displayValues = collegeData.core_values_override || globalData?.core_values || '';
  const displayMission = collegeData.mission_statement_override || globalData?.mission_statement || '';

  return (
    <div className="space-y-4">
      {/* Mission Statement - EDITABLE */}
      {(displayMission || isEditingMission) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Mission Statement</h4>
            {!isEditingMission ? (
              <button
                onClick={() => {
                  setEditedMission(displayMission);
                  setIsEditingMission(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingMission(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveMission}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingMission ? (
            <textarea
              value={editedMission}
              onChange={(e) => setEditedMission(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200 italic">
              "{displayMission}"
            </div>
          )}
          {collegeData.mission_statement_override && !isEditingMission && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Core Values */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-900">Core Values</h4>
          {!isEditingValues ? (
            <button
              onClick={() => setIsEditingValues(true)}
              className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditedValues(displayValues);
                  setIsEditingValues(false);
                }}
                className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
              >
                <XIcon className="w-3 h-3" />
                Cancel
              </button>
              <button
                onClick={handleSaveValues}
                disabled={saving}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
              >
                <Check className="w-3 h-3" />
                Save
              </button>
            </div>
          )}
        </div>

        {isEditingValues ? (
          <div>
            <p className="text-xs text-gray-500 mb-2">Enter values separated by commas</p>
            <textarea
              value={editedValues}
              onChange={(e) => setEditedValues(e.target.value)}
              placeholder="e.g., Academic excellence, Research and innovation, Service to society"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {displayValues && displayValues.split(',').map((value, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium"
              >
                {value.trim()}
              </span>
            ))}
          </div>
        )}
        {collegeData.core_values_override && (
          <p className="text-xs text-gray-500 mt-1 italic">Custom values (overriding database)</p>
        )}
      </div>
    </div>
  );
}

// Section 2: Academics Research
function AcademicsResearch({ collegeData, essaySubtype }) {
  const globalData = collegeData.global;
  const hasAIResearch = collegeData.essay_angles && collegeData.essay_angles.length > 0;

  // Edit state for each field
  const [isEditingAngles, setIsEditingAngles] = useState(false);
  const [editedAngles, setEditedAngles] = useState([]);
  const [isEditingSpecialPrograms, setIsEditingSpecialPrograms] = useState(false);
  const [editedSpecialPrograms, setEditedSpecialPrograms] = useState('');
  const [isEditingHonorsPrograms, setIsEditingHonorsPrograms] = useState(false);
  const [editedHonorsPrograms, setEditedHonorsPrograms] = useState('');
  const [saving, setSaving] = useState(false);

  // Save functions
  const saveAngles = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ essay_angles: editedAngles.filter(a => a.trim()) })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.essay_angles = editedAngles.filter(a => a.trim());
      setIsEditingAngles(false);
      toast.success('AI research updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveSpecialPrograms = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ special_programs_override: editedSpecialPrograms })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.special_programs_override = editedSpecialPrograms;
      setIsEditingSpecialPrograms(false);
      toast.success('Special programs updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveHonorsPrograms = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ honors_programs_override: editedHonorsPrograms })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.honors_programs_override = editedHonorsPrograms;
      setIsEditingHonorsPrograms(false);
      toast.success('Honors programs updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Display values (use override if exists, else global)
  const displaySpecialPrograms = collegeData.special_programs_override || globalData?.special_programs || '';
  const displayHonorsPrograms = collegeData.honors_programs_override || globalData?.honors_programs || '';

  return (
    <div className="space-y-4">
      {/* AI Verification Warning */}
      {hasAIResearch && (
        <div className="bg-amber-50 border-2 border-amber-500 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-bold text-amber-900 mb-1">
                ⚠️ VERIFICATION REQUIRED
              </h4>
              <p className="text-xs text-amber-800 leading-relaxed">
                The research below was AI-generated and <strong>MUST be verified</strong> before use.
                Check the school's official website to confirm all information is accurate and current.
                Using incorrect or outdated data could harm your application.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Essay Angles (AI suggestions) - EDITABLE */}
      {hasAIResearch && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
              <Lightbulb className="w-4 h-4 text-yellow-600" />
              <span>AI Research Suggestions</span>
            </h4>
            {!isEditingAngles ? (
              <button
                onClick={() => {
                  setEditedAngles([...collegeData.essay_angles]);
                  setIsEditingAngles(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingAngles(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveAngles}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingAngles ? (
            <div className="space-y-2">
              {editedAngles.map((angle, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <textarea
                    value={angle}
                    onChange={(e) => {
                      const updated = [...editedAngles];
                      updated[idx] = e.target.value;
                      setEditedAngles(updated);
                    }}
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  />
                  <button
                    onClick={() => {
                      const updated = editedAngles.filter((_, i) => i !== idx);
                      setEditedAngles(updated);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setEditedAngles([...editedAngles, ''])}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add suggestion
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {collegeData.essay_angles.map((angle, idx) => (
                <div key={idx} className="text-sm text-gray-700 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  {angle}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Special Programs - EDITABLE */}
      {(displaySpecialPrograms || isEditingSpecialPrograms) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Special Programs</h4>
            {!isEditingSpecialPrograms ? (
              <button
                onClick={() => {
                  setEditedSpecialPrograms(displaySpecialPrograms);
                  setIsEditingSpecialPrograms(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingSpecialPrograms(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveSpecialPrograms}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingSpecialPrograms ? (
            <textarea
              value={editedSpecialPrograms}
              onChange={(e) => setEditedSpecialPrograms(e.target.value)}
              rows={3}
              placeholder="e.g., Robertson Scholars Leadership Program, A.B. Duke Scholars Program"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displaySpecialPrograms}
            </div>
          )}
          {collegeData.special_programs_override && !isEditingSpecialPrograms && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Honors Programs - EDITABLE */}
      {(displayHonorsPrograms || isEditingHonorsPrograms) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Honors Programs</h4>
            {!isEditingHonorsPrograms ? (
              <button
                onClick={() => {
                  setEditedHonorsPrograms(displayHonorsPrograms);
                  setIsEditingHonorsPrograms(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingHonorsPrograms(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveHonorsPrograms}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingHonorsPrograms ? (
            <textarea
              value={editedHonorsPrograms}
              onChange={(e) => setEditedHonorsPrograms(e.target.value)}
              rows={3}
              placeholder="e.g., Trinity College Honors Program"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displayHonorsPrograms}
            </div>
          )}
          {collegeData.honors_programs_override && !isEditingHonorsPrograms && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Major of Interest */}
      {essaySubtype === 'why_major' && collegeData.major_of_interest && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Your Major Interest</h4>
          <div className="text-sm text-gray-700 bg-indigo-50 rounded-lg p-3 border border-indigo-200">
            {collegeData.major_of_interest}
          </div>
        </div>
      )}
    </div>
  );
}

// Section 3: Campus Research
function CampusResearch({ collegeData, essaySubtype }) {
  const globalData = collegeData.global;

  // Edit state for each field
  const [isEditingCampusCulture, setIsEditingCampusCulture] = useState(false);
  const [editedCampusCulture, setEditedCampusCulture] = useState('');
  const [isEditingStudentLife, setIsEditingStudentLife] = useState(false);
  const [editedStudentLife, setEditedStudentLife] = useState('');
  const [isEditingOrganizations, setIsEditingOrganizations] = useState(false);
  const [editedOrganizations, setEditedOrganizations] = useState('');
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editedLocation, setEditedLocation] = useState('');
  const [saving, setSaving] = useState(false);

  // Save functions
  const saveCampusCulture = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ campus_culture_override: editedCampusCulture })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.campus_culture_override = editedCampusCulture;
      setIsEditingCampusCulture(false);
      toast.success('Campus culture updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveStudentLife = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ student_life_override: editedStudentLife })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.student_life_override = editedStudentLife;
      setIsEditingStudentLife(false);
      toast.success('Student life updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveOrganizations = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ cultural_organizations_override: editedOrganizations })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.cultural_organizations_override = editedOrganizations;
      setIsEditingOrganizations(false);
      toast.success('Organizations updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveLocation = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ surrounding_area_override: editedLocation })
        .eq('id', collegeData.id);
      if (error) throw error;
      collegeData.surrounding_area_override = editedLocation;
      setIsEditingLocation(false);
      toast.success('Location info updated');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Display values (use override if exists, else global)
  const displayCampusCulture = collegeData.campus_culture_override || globalData?.campus_culture || '';
  const displayStudentLife = collegeData.student_life_override || globalData?.student_life || '';
  const displayOrganizations = collegeData.cultural_organizations_override || globalData?.cultural_organizations || '';
  const displayLocation = collegeData.surrounding_area_override || globalData?.surrounding_area || '';

  return (
    <div className="space-y-4">
      {/* Campus Culture - EDITABLE */}
      {(displayCampusCulture || isEditingCampusCulture) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Campus Culture</h4>
            {!isEditingCampusCulture ? (
              <button
                onClick={() => {
                  setEditedCampusCulture(displayCampusCulture);
                  setIsEditingCampusCulture(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingCampusCulture(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveCampusCulture}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingCampusCulture ? (
            <textarea
              value={editedCampusCulture}
              onChange={(e) => setEditedCampusCulture(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displayCampusCulture}
            </div>
          )}
          {collegeData.campus_culture_override && !isEditingCampusCulture && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Campus Culture Details (JSONB) - Display only, too complex to edit here */}
      {globalData?.campus_culture_details && Object.keys(globalData.campus_culture_details).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Culture Details</h4>
          <div className="space-y-2">
            {Object.entries(globalData.campus_culture_details).map(([key, value]) => (
              <div key={key} className="text-sm bg-white rounded-lg p-3 border border-gray-200">
                <div className="font-medium text-gray-900 capitalize mb-1">
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-gray-700">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Student Life - EDITABLE */}
      {(displayStudentLife || isEditingStudentLife) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Student Life</h4>
            {!isEditingStudentLife ? (
              <button
                onClick={() => {
                  setEditedStudentLife(displayStudentLife);
                  setIsEditingStudentLife(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingStudentLife(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveStudentLife}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingStudentLife ? (
            <textarea
              value={editedStudentLife}
              onChange={(e) => setEditedStudentLife(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displayStudentLife}
            </div>
          )}
          {collegeData.student_life_override && !isEditingStudentLife && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Cultural Organizations - EDITABLE */}
      {(displayOrganizations || isEditingOrganizations) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Cultural Organizations</h4>
            {!isEditingOrganizations ? (
              <button
                onClick={() => {
                  setEditedOrganizations(displayOrganizations);
                  setIsEditingOrganizations(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingOrganizations(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveOrganizations}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingOrganizations ? (
            <textarea
              value={editedOrganizations}
              onChange={(e) => setEditedOrganizations(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displayOrganizations}
            </div>
          )}
          {collegeData.cultural_organizations_override && !isEditingOrganizations && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}

      {/* Location Context - EDITABLE */}
      {(displayLocation || isEditingLocation) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Location</h4>
            {!isEditingLocation ? (
              <button
                onClick={() => {
                  setEditedLocation(displayLocation);
                  setIsEditingLocation(true);
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditingLocation(false)}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <XIcon className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={saveLocation}
                  disabled={saving}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" />
                  Save
                </button>
              </div>
            )}
          </div>

          {isEditingLocation ? (
            <textarea
              value={editedLocation}
              onChange={(e) => setEditedLocation(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
              {displayLocation}
            </div>
          )}
          {collegeData.surrounding_area_override && !isEditingLocation && (
            <p className="text-xs text-gray-500 mt-1 italic">Custom (overriding database)</p>
          )}
        </div>
      )}
    </div>
  );
}

// Section 4: Personal Research
function PersonalResearch({ collegeData }) {
  return (
    <div className="space-y-4">
      {/* Why Interested */}
      {collegeData.why_interested && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Why You're Interested</h4>
          <div className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
            {collegeData.why_interested}
          </div>
        </div>
      )}

      {/* Strategic Fit Analysis */}
      {collegeData.strategic_fit_analysis && Object.keys(collegeData.strategic_fit_analysis).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center space-x-2">
            <Target className="w-4 h-4 text-green-600" />
            <span>Strategic Fit</span>
          </h4>
          <div className="text-sm text-gray-700 bg-green-50 rounded-lg p-3 border border-green-200">
            {collegeData.strategic_fit_analysis.summary || JSON.stringify(collegeData.strategic_fit_analysis)}
          </div>
        </div>
      )}

      {/* Your Advantages */}
      {collegeData.your_advantages && collegeData.your_advantages.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Your Advantages</h4>
          <ul className="space-y-2">
            {collegeData.your_advantages.map((advantage, idx) => (
              <li key={idx} className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200 flex items-start space-x-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>{advantage}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Need Story Help */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Need Story Inspiration?</h4>
        <p className="text-sm text-blue-800 mb-3">
          These essay interviews can help you discover vivid moments for your personal section.
        </p>
        <div className="space-y-2">
          <a
            href="/essays/character-identity"
            target="_blank"
            className="block text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            → Character & Identity
          </a>
          <a
            href="/essays/moments-that-matter"
            target="_blank"
            className="block text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            → Moments that Matter
          </a>
          <a
            href="/essays/values-beliefs"
            target="_blank"
            className="block text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            → Values & Beliefs
          </a>
        </div>
      </div>
    </div>
  );
}

// Sections 5-6: Strategic Research
function StrategicResearch({ collegeData }) {
  return (
    <div className="space-y-4">
      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <h4 className="text-sm font-semibold text-purple-900 mb-2">📋 Use Your Research</h4>
        <p className="text-sm text-purple-800">
          Draw from all the research you've done in earlier sections to create compelling rhetorical devices and bridge sentences.
        </p>
      </div>

      {/* Essay Angles */}
      {collegeData.essay_angles && collegeData.essay_angles.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Suggested Angles</h4>
          <div className="space-y-2">
            {collegeData.essay_angles.slice(0, 3).map((angle, idx) => (
              <div key={idx} className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
                {angle}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Section 7: Compose Research
function ComposeResearch({ collegeData }) {
  return (
    <div className="space-y-4">
      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
        <h4 className="text-sm font-semibold text-green-900 mb-2">✨ Final Assembly</h4>
        <p className="text-sm text-green-800">
          Choose an essay style that matches your voice and how you naturally communicate about your interests.
        </p>
      </div>

      {/* School Summary */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">School Quick Reference</h4>
        <div className="space-y-2 text-sm">
          {collegeData.global?.mission_statement && (
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="font-medium text-gray-700 mb-1">Mission:</div>
              <div className="text-gray-600 italic">{collegeData.global.mission_statement}</div>
            </div>
          )}
          {collegeData.global?.core_values && (
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="font-medium text-gray-700 mb-1">Values:</div>
              <div className="text-gray-600">{collegeData.global.core_values}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
